import React, { useEffect, useState, useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box } from "@mui/material";
import { lightenHexToRgba } from "../../../components/Common/Utils/Utils";
import dayjs from "dayjs";
import { useAuthContext } from "../../../context/AuthProvider";
import HCDataGrouping from "highcharts/modules/data";
import HCBoost from "highcharts/modules/boost";

HCDataGrouping(Highcharts);
HCBoost(Highcharts);

const SeperateGraph = ({
  selectedGranularity,
  newData,
  scrapDate,
  showScrapPart,
  categories = [],
}) => {
  const [options, setOptions] = useState(null);
  const chartRef = useRef(null);
  const { QCFull } = useAuthContext();

  const timeFlag = () => {
    if (selectedGranularity === "Daily" || selectedGranularity === "Monthly") {
      return true;
    } else if (newData?.[0]?.actualValues?.length > 1200) {
      return true;
    } else {
      return false;
    }
  };

  function getIndicesMultiple(array) {
    const count = Math.floor(array.length / 7);
    if (count === 0) return [0];

    return array
      .map((_, index) => index)
      .filter((index) => index % count === 0)
      .slice(0, 7);
  }

  useEffect(() => {
    let initialMin, initialMax;
    const yAxisHeight = (100 - (newData.length - 1) * 5) / newData.length;
    const yAxisSpacing = 5;

    // const categoriesUnix = categories?.map((cat) => dayjs(cat).unix() * 1000);
    const scrapDatesUnix = scrapDate?.map((date) => dayjs(date).unix() * 1000);

    const yAxisGraph = newData?.map((dataSet, index) => ({
      title: {
        text: "",
      },
      top: `${index * (yAxisHeight + yAxisSpacing)}%`,
      height: `${yAxisHeight}%`,
      offset: 4,
      lineWidth: 1,
      gridLineWidth: 0,
      tickWidth: 1,
      tickLength: 8,
      labels: {
        style: {
          color: dataSet.color,
          fontSize: "10px",
          fontWeight: "600",
        },
        formatter: function () {
          if (this.value >= 1000) {
            return this.value / 1000 + "k";
          } else {
            return this.value;
          }
        },
      },
      plotBands: [
        {
          from: dataSet.lowerLimit,
          to: dataSet.upperlimit,
          color: lightenHexToRgba(dataSet.color),
          zIndex: 0,
        },
      ],
    }));

    const seriesData = newData?.flatMap((dataSet, index) => [
      {
        name: `Actual Values ${dataSet.factorName}`,
        data: dataSet.actualValues ? dataSet.actualValues : [],
        color: dataSet.color,
        marker: {
          enabled: false,
        },
        yAxis: index,
        connectNulls: true,
        turboThreshold: 0,
      },
      {
        name: `Mean ${dataSet.factorName}`,
        data: dataSet.averageValues ? dataSet.averageValues : [],
        color: dataSet.color,
        dashStyle: "Dash",
        marker: {
          enabled: false,
        },
        yAxis: index,
        connectNulls: true,
        turboThreshold: 0,
      },
    ]);

    const plotLines = showScrapPart
      ? scrapDate?.map((unixTime) => ({
          color: "#9f9f9f",
          value: categories.indexOf(unixTime),
          width: 1,
          zIndex: 1,
        }))
      : [];

    const timezoneOffset = new Date().getTimezoneOffset();

    setOptions({
      chart: {
        type: "line",
        height: QCFull
          ? window.screen.height - window.screen.height / 3
          : window.screen.height - window.screen.height / 2.1,
        zoomType: "x",
        zoomKey: "ctrl",
        panning: {
          enabled: true,
          type: "x",
        },
        zooming: {
          mouseWheel: {
            enabled: false,
          },
        },
        panKey: "shift",
        // events: {
        //   load: function () {
        //     const chart = this;
        //     chartRef.current = chart.container;

        //     const xAxis = chart.xAxis[0];
        //     initialMin = xAxis.min;
        //     initialMax = xAxis.max;

        //     chartRef.current.addEventListener("wheel", (event) => {
        //       event.preventDefault();
        //       const delta = event.deltaY || event.detail || event.wheelDelta;
        //       const xAxis = chart.xAxis[0];
        //       const extremes = xAxis.getExtremes();
        //       const range = extremes.max - extremes.min;
        //       const zoomRatio = delta > 0 ? 1.2 : 0.8; // Adjust the ratio for more or less zoom

        //       let newMin = extremes.min + (range * (1 - zoomRatio)) / 2;
        //       let newMax = extremes.max - (range * (1 - zoomRatio)) / 2;

        //       // Ensure the new range does not exceed initial range
        //       if (newMin < initialMin) newMin = initialMin;
        //       if (newMax > initialMax) newMax = initialMax;

        //       xAxis.setExtremes(newMin, newMax);
        //     });
        //   },
        // },
      },
      boost: {
        seriesThreshold: 100000,
      },
      credits: {
        enabled: false,
      },
      title: {
        text: null,
      },
      tooltip: {
        formatter: function () {
          if (this.point.value === null) {
            return "Null";
          }
          const formattedValue = this.point.y.toFixed(2);
          const xAxisLabel = dayjs(this.x).format(
            selectedGranularity === "Minute" || selectedGranularity === "Hourly"
              ? "YYYY-MM-DD HH:mm:ss"
              : "YYYY-MM-DD HH:mm"
          );
          let tooltipText =
            '<p style="font-size: 10px;">' +
            xAxisLabel +
            "</p>" +
            "<br/>" +
            this.series.name +
            "<b>: " +
            formattedValue +
            "</b>";

          const scrapDatesInRange = scrapDatesUnix.filter(
            (date) =>
              Math.abs(date - dayjs(this.x).unix() * 1000) < 30 * 60 * 1000
          );
          if (scrapDatesInRange.length > 0 && showScrapPart) {
            tooltipText += `<br/><span style="color: #EF7B64;">Scrap Dates:</span>`;
            scrapDatesInRange.forEach((scrapDate) => {
              tooltipText += `<br/><span style="color: #EF7B64;">${dayjs(
                scrapDate
              ).format("YYYY-MM-DD HH:mm:ss")}</span>`;
            });
          }

          return tooltipText;
        },
      },
      xAxis: {
        type: "category",
        categories: categories,
        labels: {
          style: {
            color: "#404040",
            fontSize: "10px",
            fontWeight: "600",
          },
          formatter: function () {
            return dayjs(this.value).format(
              timeFlag() ? "YYYY-MM-DD HH:mm" : "YYYY-MM-DD HH:mm:ss"
            );
          },
        },
        tickPositions: getIndicesMultiple(categories),
        lineWidth: 0,
        gridLineWidth: 0,
        plotLines: showScrapPart ? plotLines : [],
        dateTimeLabelFormats: {
          second: "%Y-%m-%d %H:%M:%S",
          minute: "%Y-%m-%d %H:%M:%S",
          hour: "%Y-%m-%d %H:%M:%S",
          day: timeFlag() ? "%Y-%m-%d %H:%M" : "%Y-%m-%d %H:%M:%S",
          week: timeFlag() ? "%Y-%m-%d %H:%M" : "%Y-%m-%d %H:%M:%S",
          month: timeFlag() ? "%Y-%m-%d %H:%M" : "%Y-%m-%d %H:%M:%S",
          year: "%Y-%m-%d",
        },
      },
      yAxis: yAxisGraph,
      series: seriesData,
      legend: {
        enabled: false,
      },
      time: {
        timezoneOffset: timezoneOffset,
      },
    });
  }, [newData, scrapDate, showScrapPart, selectedGranularity, QCFull]);

  return (
    <Box sx={{ height: "100%", overflow: "auto" }}>
      {options && <HighchartsReact highcharts={Highcharts} options={options} />}
    </Box>
  );
};
export default SeperateGraph;
